import React from 'react'
import styled from 'styled-components'
import { darken } from 'polished'
import { useTranslation } from 'react-i18next'
import { NavLink, Link as HistoryLink, useHistory, useLocation } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import { ArrowLeft } from 'react-feather'
import { RowBetween } from '../Row'
import SettingsTab from '../Settings'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'state'
import { resetMintState } from 'state/mint/actions'
import { resetMintState as resetMintV3State } from 'state/mint/v3/actions'
import { TYPE } from 'theme'
import useTheme from 'hooks/useTheme'
import { StyledTabButton } from 'pages/Stake/EarnSTHYPE'

export const Tabs = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  border-radius: 3rem;
  justify-content: space-evenly;
`

const activeClassName = 'ACTIVE'

export const StyledNavLink = styled(NavLink).attrs({
  activeClassName,
})`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  justify-content: center;
  height: 3rem;
  border-radius: 3rem;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  color: ${({ theme }) => theme.text3};
  font-size: 20px;

  &.${activeClassName} {
    border-radius: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.text1};
  }

  :hover,
  :focus {
    color: ${({ theme }) => darken(0.1, theme.text1)};
  }
`

const ActiveText = styled.div`
  font-weight: 500;
  font-size: 20px;
`

const StyledArrowLeft = styled(ArrowLeft)`
  color: ${({ theme }) => theme.text1};
`

export function SwapPoolTabs({ active }: { active: 'swap' | 'pool' }) {
  const { t } = useTranslation()
  return (
    <Tabs style={{ marginBottom: '20px', display: 'none', padding: '1rem 1rem 0 1rem' }}>
      <StyledNavLink id={`swap-nav-link`} to={'/swap'} isActive={() => active === 'swap'}>
        {t('swap')}
      </StyledNavLink>
      <StyledNavLink id={`pool-nav-link`} to={'/pool'} isActive={() => active === 'pool'}>
        {t('pool')}
      </StyledNavLink>
    </Tabs>
  )
}

export function FindPoolTabs({ origin }: { origin: string }) {
  return (
    <Tabs>
      <RowBetween style={{ padding: '1rem 1rem 0 1rem' }}>
        <HistoryLink to={origin}>
          <StyledArrowLeft />
        </HistoryLink>
        <ActiveText>Import Pool</ActiveText>
        <SettingsTab />
      </RowBetween>
    </Tabs>
  )
}

export function AddRemoveTabs({
  adding,
  creating,
  positionID,
  v2v3Switch = false,
}: {
  adding: boolean
  creating: boolean
  positionID?: string | undefined
  v2v3Switch?: boolean
}) {
  const theme = useTheme()
  const history = useHistory()
  const location = useLocation()
  const params: any = useParams()
  const currencyIdA = params?.currencyIdA ?? null
  const currencyIdB = params?.currencyIdB ?? null
  const isV2 = location.pathname.toLowerCase().includes('v2')

  const switchRoute = `/add${isV2 ? '' : '/v2'}${currencyIdA ? `/${currencyIdA}` : ''}${
    currencyIdB ? `/${currencyIdB}` : ''
  }`

  // reset states on back
  const dispatch = useDispatch<AppDispatch>()

  return (
    <>
      <Tabs>
        <RowBetween style={{ padding: '1rem 1rem 0 1rem' }}>
          <HistoryLink
            to={!isV2 ? '/pool' + (!!positionID ? `/${positionID.toString()}` : '') : '/pool/v2'}
            onClick={() => {
              if (adding) {
                // not 100% sure both of these are needed
                dispatch(resetMintState())
                dispatch(resetMintV3State())
              }
            }}
          >
            <StyledArrowLeft stroke={theme.text2} />
          </HistoryLink>
          <TYPE.mediumHeader fontWeight={500} fontSize={20}>
            {creating ? 'Create a pair' : adding ? 'Add Liquidity' : 'Remove Liquidity'}
          </TYPE.mediumHeader>
          <SettingsTab />
        </RowBetween>
      </Tabs>
      {v2v3Switch && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '16px' }}>
          <Tabs>
            <StyledTabButton isActive={isV2} onClick={() => history.push(switchRoute)}>
              Position V2
            </StyledTabButton>
            <StyledTabButton isActive={!isV2} onClick={() => history.push(switchRoute)}>
              Position V3
            </StyledTabButton>
          </Tabs>
        </div>
      )}
    </>
  )
}
